import { sendAnalytics } from '@audio-network/analytics'
import { usePlayerApi } from '@audio-network/player'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { GetServerSideProps } from 'next/types'
import React, { FormEvent, useEffect, useState } from 'react'

import { clientGetAlbumsWithCount, clientGetTracks } from '@/client'
import { AlbumList, Header, LoadMore } from '@/components/pages/albums'
import { getGenres } from '@/pages/api/musicapp/_services/music'
import {
	getAlbumsWithCount,
	getTracks,
} from '@/pages/api/musicapp/_services/musicService'
import formatTitleByLocale from '@/utils/formatting/formatTitleByLocale'
import { getIpAddressFromHeader } from '@/utils/getIpAddressFromHeader'
import { withGlobalTranslations } from '@/utils/i18n/withGlobalTranslations'
import { getTopTracksFromAlbums } from '@/utils/music/getTopTracksFromAlbums'

import { getCatalogues } from '../api/musicapp/_services/catalogueService'

export default function Albums({
	locale,
	albumsData,
	genres,
	canLoadMore,
	initialAlbumTracks,
	initialGenreId,
	initialPage,
}: {
	locale: string
	albumsData: AlbumResponse
	genres: Genre[]
	canLoadMore: boolean
	initialAlbumTracks: Track[]
	initialGenreId: number | null
	initialPage: number
}): JSX.Element {
	const { initialiseQueue } = usePlayerApi()
	const router = useRouter()

	const [canLoadMoreAlbums, setCanLoadMoreAlbums] = useState(canLoadMore)
	const [loading, setLoading] = useState(false)
	const [page, setPage] = useState(initialPage)
	const [pageQueue, setPageQueue] = useState(initialAlbumTracks)
	const [albumList, setAlbumList] = useState(albumsData.albums)
	const [genreId, setGenreId] = useState<number | null>(initialGenreId)
	const [albumCount, setAlbumCount] = useState(albumsData.albumCount)

	useEffect(() => {
		sendAnalytics({
			noun: 'All Albums Page',
			pastTenseVerb: 'Viewed',
		})
	}, [])

	useEffect(() => {
		initialiseQueue(pageQueue)
	}, [pageQueue, initialiseQueue])

	useEffect(() => {
		setCanLoadMoreAlbums(albumList.length < albumCount)
	}, [albumCount, albumList.length])

	useEffect(() => {
		const handleNavItemClick = (): void => {
			setPage(1)
			setGenreId(null)
		}
		const navItems = document.querySelectorAll('[data-navitem="albums"]')
		navItems.forEach((navItem) => {
			navItem.addEventListener('click', handleNavItemClick, { once: true })
		})

		return () => {
			navItems.forEach((navItem) => {
				navItem.removeEventListener('click', handleNavItemClick)
			})
		}
	}, [router])

	const handleLoadMore = async (): Promise<void> => {
		const nextPage = page + 1

		sendAnalytics({
			noun: 'Albums Paging',
			pastTenseVerb: 'Clicked',
			eventParameters: {
				Take: 24, // hard coded limit
				Page: nextPage,
			},
		})

		setLoading(true)

		// Update URL with new page parameter
		const queryParams: Record<string, string> = { page: nextPage.toString() }
		if (genreId) {
			queryParams.genreId = genreId.toString()
		}
		router.push(
			{
				pathname: router.pathname,
				query: queryParams,
			},
			undefined,
			{ shallow: true }
		)

		const nextAlbumsData: AlbumResponse = await clientGetAlbumsWithCount(
			page + 1,
			locale,
			genreId
		)
		if (typeof nextAlbumsData !== 'undefined') {
			const nextAlbumTrackIds = getTopTracksFromAlbums(nextAlbumsData.albums)
			const nextPageAlbumPreviewTracks = await clientGetTracks(
				nextAlbumTrackIds
			)
			setAlbumList([...albumList, ...nextAlbumsData?.albums])
			setPageQueue([...pageQueue, ...nextPageAlbumPreviewTracks])
			setPage(nextPage)
		}

		setLoading(false)
	}

	const handleSelect = async (event: FormEvent): Promise<void> => {
		const { value } = event.target as HTMLSelectElement

		const genreId = value !== 'all' ? parseInt(value, 10) : null

		setGenreId(genreId)

		const genreName =
			genreId && genres.find((genre) => genre.id === genreId).name

		sendAnalytics({
			noun: 'Albums Filter',
			pastTenseVerb: 'Clicked',
			eventParameters: {
				Name: genreName ?? 'All Albums',
				GenreId: genreId,
			},
		})

		// Update URL with new genre parameter and reset page to 1
		const queryParams: Record<string, string> = { page: '1' }
		if (genreId) {
			queryParams.genreId = genreId.toString()
		}
		router.push(
			{
				pathname: router.pathname,
				query: queryParams,
			},
			undefined,
			{ shallow: true }
		)

		// TODO: Address what "loading" really means here. Do we need skeletons?
		setLoading(true)

		const filteredAlbumsData: AlbumResponse = await clientGetAlbumsWithCount(
			1,
			locale,
			genreId
		)

		if (typeof filteredAlbumsData !== 'undefined') {
			const filteredAlbumTrackIds = getTopTracksFromAlbums(
				filteredAlbumsData.albums
			)
			const filteredAlbumPreviewTracks = await clientGetTracks(
				filteredAlbumTrackIds
			)
			setAlbumList(filteredAlbumsData?.albums)
			setAlbumCount(filteredAlbumsData.albumCount)
			setPageQueue([...filteredAlbumPreviewTracks])
			setPage(1)

			setLoading(false)
		}
	}

	return (
		<>
			<Head>
				<title>{formatTitleByLocale(locale)}</title>
			</Head>

			<Header />

			<AlbumList
				albums={albumList}
				albumCount={albumCount}
				genres={genres}
				onSelect={handleSelect}
				locale={locale}
				albumPreviewTracks={pageQueue}
				defaultGenre={genreId}
			/>

			{canLoadMoreAlbums && (
				<LoadMore loading={loading} onLoadMore={handleLoadMore} />
			)}
		</>
	)
}

export const getServerSideProps: GetServerSideProps = async ({
	req,
	locale,
	query,
}) => {
	const { genreId, page } = query

	const initialGenreId = genreId ? parseInt(genreId as string, 10) : null
	const initialPage = page ? parseInt(page as string, 10) : 1

	const ipAddress = getIpAddressFromHeader(
		req.headers,
		req.socket.remoteAddress
	)

	const viewableCatalogues = await getCatalogues(ipAddress)

	const albumsData = await getAlbumsWithCount(
		initialPage,
		locale,
		viewableCatalogues,
		initialGenreId,
		true
	)

	const requiredGenresById = [
		1, // Blues
		2, // Christmas
		3, // Country
		1034, // Documentary
		1043, // Drama
		4, // Electronic
		5, // Folk
		8, // Hip Hop/R&B
		9, // Indie
		7, // Global Music
		11, // Jazz
		12, // Latin
		1078, // Lifestyle
		13, // Orchestral
		14, // Pop
		16, // Rock
	]

	const genres = await getGenres({ locale }).then((response) => {
		return response.filter((genre) => requiredGenresById.includes(genre.id))
	})

	const canLoadMore =
		albumsData && albumsData.albums.length < albumsData.albumCount

	const initialAlbumTrackIds: number[] =
		albumsData && getTopTracksFromAlbums(albumsData.albums)

	const initialAlbumTracks =
		initialAlbumTrackIds && (await getTracks(initialAlbumTrackIds.map(String)))

	return {
		props: {
			...(await withGlobalTranslations(locale, ['albums'])),
			locale,
			albumsData,
			genres,
			canLoadMore,
			initialAlbumTracks,
			initialGenreId,
			initialPage,
		},
	}
}
